import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Coins, Twitter } from 'lucide-react';
import HowItWorksModal from './HowItWorksModal';
import SupportedCharitiesModal from './SupportedCharitiesModal';
import FAQModal from './FAQModal';
import TermsModal from './TermsModal';
import ContactModal from './ContactModal';
import './Footer.css';

const Footer = ({ isHidden = false }) => {
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showCharitiesModal, setShowCharitiesModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  
  if (isHidden) {
    return null;
  }
  
  return (
    <footer className="footer">
      <div className="footer-background">
        <div className="footer-gradient"></div>
      </div>
      
      <div className="container">
        <div className="footer-content">
          <motion.div
            className="footer-main"
            initial={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="footer-brand">
              <h3>📦 GuiltBox</h3>
              <p>Transforming confessions into meaningful impact through crypto tokens and charity support.</p>
              <div className="social-links">
                <motion.a
                  href="https://x.com/guiltboxme"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Twitter size={20} />
                </motion.a>
                <motion.a
                  href="https://zora.co/@guiltboxme"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  title="GuiltBox on Zora"
                >
                  <Coins size={20} />
                </motion.a>
              </div>
            </div>

            <div className="footer-links">
              <div className="link-group">
                <h4>Platform</h4>
                <ul>
                  <li><a href="#submit">Submit Confession</a></li>
                  <li><a href="#confessions">Browse Gallery</a></li>
                  <li>
                    <a 
                      href="#how-it-works" 
                      onClick={(e) => {
                        e.preventDefault();
                        setShowHowItWorksModal(true);
                      }}
                    >
                      How It Works
                    </a>
                  </li>
                  <li>
                    <a 
                      href="#faq" 
                      onClick={(e) => {
                        e.preventDefault();
                        setShowFAQModal(true);
                      }}
                    >
                      FAQ
                    </a>
                  </li>
                </ul>
              </div>

              <div className="link-group">
                <h4>Community</h4>
                <ul>
                  <li>
                    <a 
                      href="#supported-charities" 
                      onClick={(e) => {
                        e.preventDefault();
                        setShowCharitiesModal(true);
                      }}
                    >
                      Supported Charities
                    </a>
                  </li>
                  <li><a href="https://zora.co/@guiltboxme" target="_blank" rel="noopener noreferrer">GuiltBox on Zora</a></li>
                  <li>
                    <a 
                      href="#contact" 
                      onClick={(e) => {
                        e.preventDefault();
                        setShowContactModal(true);
                      }}
                    >
                      Contact us
                    </a>
                  </li>
                  <li>
                    <a 
                      href="#terms" 
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTermsModal(true);
                      }}
                    >
                      Terms
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="footer-stats"
            initial={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="stat-item">
              <div className="stat-icon">💝</div>
              <div className="stat-info">
                <span className="stat-number">9</span>
                <span className="stat-label">Charities Supported</span>
              </div>
            </div>
            
            <div className="stat-item">
              <div className="stat-icon">📖</div>
              <div className="stat-info">
                <span className="stat-number">∞</span>
                <span className="stat-label">Stories Shared</span>
              </div>
            </div>
            
            <div className="stat-item">
              <div className="stat-icon">🔍</div>
              <div className="stat-info">
                <span className="stat-number">100%</span>
                <span className="stat-label">Transparent</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="footer-bottom"
            initial={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="footer-bottom-content">
              <p>
                Made with <Heart size={16} className="heart-icon" /> for a better world
              </p>
              <div className="footer-tech">
                <span>Built on</span>
                <div className="tech-badges">
                  <span className="tech-badge">Ethereum</span>
                  <span className="tech-badge">IPFS</span>
                  <span className="tech-badge">Zora</span>
                  <span className="tech-badge">0xSplits</span>
                </div>
              </div>
            </div>
            
            <div className="footer-copyright">
              <p>&copy; 2024 GuiltBox. All rights reserved.</p>
            </div>
          </motion.div>
        </div>
      </div>
      
      <HowItWorksModal
        isOpen={showHowItWorksModal}
        onClose={() => setShowHowItWorksModal(false)}
      />
      
      <SupportedCharitiesModal
        isOpen={showCharitiesModal}
        onClose={() => setShowCharitiesModal(false)}
      />
      
      <FAQModal
        isOpen={showFAQModal}
        onClose={() => setShowFAQModal(false)}
      />
      
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
      
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </footer>
  );
};

export default Footer;